/**
 * Common Timezones
 *
 * List of commonly used timezones for user preference selection.
 * Uses IANA timezone identifiers.
 */

export interface TimezoneOption {
  value: string
  label: string
  offset: string
}

/**
 * List of common timezones grouped by region
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  // Europe
  { value: 'Europe/Rome', label: 'Roma', offset: 'CET/CEST' },
  { value: 'Europe/London', label: 'Londra', offset: 'GMT/BST' },
  { value: 'Europe/Paris', label: 'Parigi', offset: 'CET/CEST' },
  { value: 'Europe/Berlin', label: 'Berlino', offset: 'CET/CEST' },
  { value: 'Europe/Madrid', label: 'Madrid', offset: 'CET/CEST' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', offset: 'CET/CEST' },
  { value: 'Europe/Zurich', label: 'Zurigo', offset: 'CET/CEST' },
  { value: 'Europe/Vienna', label: 'Vienna', offset: 'CET/CEST' },
  { value: 'Europe/Brussels', label: 'Bruxelles', offset: 'CET/CEST' },
  { value: 'Europe/Lisbon', label: 'Lisbona', offset: 'WET/WEST' },
  { value: 'Europe/Athens', label: 'Atene', offset: 'EET/EEST' },
  { value: 'Europe/Moscow', label: 'Mosca', offset: 'MSK' },

  // Americas
  { value: 'America/New_York', label: 'New York', offset: 'EST/EDT' },
  { value: 'America/Los_Angeles', label: 'Los Angeles', offset: 'PST/PDT' },
  { value: 'America/Chicago', label: 'Chicago', offset: 'CST/CDT' },
  { value: 'America/Denver', label: 'Denver', offset: 'MST/MDT' },
  { value: 'America/Toronto', label: 'Toronto', offset: 'EST/EDT' },
  { value: 'America/Vancouver', label: 'Vancouver', offset: 'PST/PDT' },
  { value: 'America/Mexico_City', label: 'Città del Messico', offset: 'CST/CDT' },
  { value: 'America/Sao_Paulo', label: 'San Paolo', offset: 'BRT' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', offset: 'ART' },

  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo', offset: 'JST' },
  { value: 'Asia/Shanghai', label: 'Shanghai', offset: 'CST' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', offset: 'HKT' },
  { value: 'Asia/Singapore', label: 'Singapore', offset: 'SGT' },
  { value: 'Asia/Dubai', label: 'Dubai', offset: 'GST' },
  { value: 'Asia/Kolkata', label: 'Mumbai', offset: 'IST' },
  { value: 'Asia/Seoul', label: 'Seoul', offset: 'KST' },
  { value: 'Asia/Bangkok', label: 'Bangkok', offset: 'ICT' },

  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney', offset: 'AEST/AEDT' },
  { value: 'Australia/Melbourne', label: 'Melbourne', offset: 'AEST/AEDT' },
  { value: 'Australia/Perth', label: 'Perth', offset: 'AWST' },
  { value: 'Pacific/Auckland', label: 'Auckland', offset: 'NZST/NZDT' },

  // Special
  { value: 'UTC', label: 'UTC', offset: 'UTC' },
]

/**
 * Get the browser's default timezone
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Get current time in a specific timezone
 */
export function getCurrentTimeInTimezone(timezone: string): string {
  try {
    return new Date().toLocaleTimeString('it-IT', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '--:--'
  }
}

/**
 * Get UTC offset for a timezone (e.g., "+01:00", "-05:00")
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const date = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(date)
    const offsetPart = parts.find(p => p.type === 'timeZoneName')
    return offsetPart?.value || ''
  } catch {
    return ''
  }
}

/**
 * Find timezone option by value
 */
export function findTimezoneOption(value: string): TimezoneOption | undefined {
  return COMMON_TIMEZONES.find(tz => tz.value === value)
}

/**
 * Check if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch {
    return false
  }
}
